import { useEffect, useMemo, useState } from 'react';
import {
  DiscordConnectPanel,
  type DiscordConnectPanelStatus,
} from '../components/discord-connect-panel';
import { HTTPStatusError } from '../lib/chat-api';
import { completeDiscordConnect } from '../lib/discord';

const discordConnectMessageType = 'cueboard-discord-connect-result';

type CallbackStatus = DiscordConnectPanelStatus;

function readProviderError(searchParams: URLSearchParams): string | null {
  const errorDescription = searchParams.get('error_description')?.trim();
  if (errorDescription) {
    return errorDescription;
  }
  const error = searchParams.get('error')?.trim();
  if (error) {
    return `Discord connection failed: ${error}`;
  }
  return null;
}

function readHandoffScheme(stateToken: string): string | null {
  const parts = stateToken.trim().split('.', 2);
  if (parts.length !== 2 || !parts[1]) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(padded)) as { s?: unknown };
    const scheme = typeof payload.s === 'string' ? payload.s.trim() : '';
    if (
      scheme === 'bridge' ||
      scheme === 'bridge-dev' ||
      scheme === 'bridge-local' ||
      scheme === 'bridge-debug-local'
    ) {
      return scheme;
    }
  } catch {
    return null;
  }

  return null;
}

function redirectToApp(
  scheme: string,
  status: 'success' | 'error',
  message: string
) {
  const query = new URLSearchParams({ status, message });
  window.location.replace(
    `${scheme}://integration/discord?${query.toString()}`
  );
}

export function DiscordConnectCallbackPage() {
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [message, setMessage] = useState('Finishing Discord connection...');

  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const notifyOpener = (payload: Record<string, unknown>) => {
      if (!window.opener) {
        return;
      }
      window.opener.postMessage(
        { type: discordConnectMessageType, ...payload },
        window.location.origin
      );
    };

    const closeSoon = () => {
      window.setTimeout(() => window.close(), 150);
    };

    const finishWithError = (text: string) => {
      if (cancelled) {
        return;
      }
      const scheme = readHandoffScheme(searchParams.get('state')?.trim() ?? '');
      setStatus('error');
      setMessage(text);
      notifyOpener({ ok: false, message: text });
      if (scheme) {
        window.setTimeout(() => redirectToApp(scheme, 'error', text), 80);
        return;
      }
    };

    const finishWithSuccess = async () => {
      const providerError = readProviderError(searchParams);
      if (providerError) {
        finishWithError(providerError);
        return;
      }

      const code = searchParams.get('code')?.trim() ?? '';
      const state = searchParams.get('state')?.trim() ?? '';
      if (!code || !state) {
        finishWithError(
          'Discord did not send everything Bridge needs. Please return to Bridge and try again.'
        );
        return;
      }

      try {
        const result = await completeDiscordConnect(code, state);
        if (cancelled) {
          return;
        }

        const successMessage =
          result.message?.trim() || 'Discord connected successfully.';
        setStatus('success');
        setMessage(successMessage);
        notifyOpener({
          ok: true,
          message: successMessage,
          discord_user_id: result.discord_user_id,
          discord_username: result.discord_username,
        });
        const redirectURL = result.redirect_url?.trim();
        if (redirectURL) {
          window.setTimeout(() => {
            window.location.replace(redirectURL);
          }, 120);
          return;
        }
        closeSoon();
      } catch (error) {
        if (cancelled) {
          return;
        }
        let text = 'Failed to complete Discord connection.';
        if (error instanceof HTTPStatusError) {
          text = error.message;
        } else if (error instanceof Error && error.message.trim()) {
          text = error.message;
        }
        finishWithError(text);
      }
    };

    void finishWithSuccess();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  return (
    <DiscordConnectPanel
      status={status}
      title={
        status === 'loading'
          ? 'Connecting Discord'
          : status === 'success'
            ? 'Discord connected to Bridge'
            : 'Discord connection failed'
      }
      message={message}
      helperText={
        status === 'loading'
          ? 'Bridge is finishing the Discord connection. Keep this window open for a moment.'
          : status === 'success'
            ? 'Return to Bridge to continue. You can close this window if it stays open.'
            : 'Return to Bridge and try connecting Discord again.'
      }
    />
  );
}
